import React, { useState, useEffect } from 'react';
import {
  Box,
  Heading,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Switch,
  Badge,
  Avatar,
  Spinner,
  Alert,
  AlertIcon,
  useToast,
  HStack,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Button,
  ButtonGroup,
  Divider,
  Flex,
  IconButton,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
} from '@chakra-ui/react';
import { DeleteIcon } from '@chakra-ui/icons';
import { userService, s3Service } from '../services/api';
import AuditLogTable from '../components/AuditLogTable';

function AdminPanel() {
  const [users, setUsers] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pendingLoading, setPendingLoading] = useState(true);
  const [auditLoading, setAuditLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pendingError, setPendingError] = useState(null);
  const [auditError, setAuditError] = useState(null);
  const [processing, setProcessing] = useState({});
  const [userToDelete, setUserToDelete] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();

  useEffect(() => {
    fetchUsers();
    fetchPendingUsers();
    fetchAuditLogs();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const users = await userService.getAllUsers();
      setUsers(users);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to load users. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingUsers = async () => {
    try {
      setPendingLoading(true);
      setPendingError(null);
      
      const pendingUsers = await userService.getPendingUsers();
      setPendingUsers(pendingUsers);
    } catch (err) {
      console.error('Error fetching pending users:', err);
      setPendingError('Failed to load pending user requests. Please try again.');
    } finally {
      setPendingLoading(false);
    }
  };
  
  const fetchAuditLogs = async () => {
    try {
      setAuditLoading(true);
      setAuditError(null);
      
      const logs = await s3Service.getAuditLogs();
      setAuditLogs(logs);
    } catch (err) {
      console.error('Error fetching audit logs:', err);
      setAuditError('Failed to load audit logs. Please try again.');
    } finally {
      setAuditLoading(false);
    }
  };

  const handleToggleAdmin = async (userId) => {
    try {
      setProcessing(prev => ({ ...prev, [userId]: true }));
      
      const updatedUser = await userService.toggleAdminStatus(userId);
      
      // Update the local state
      setUsers(prevUsers =>
        prevUsers.map(user =>
          user.id === userId ? { ...user, is_admin: updatedUser.is_admin } : user
        )
      );
      
      toast({
        title: `User updated`,
        description: `Admin status ${updatedUser.is_admin ? 'granted to' : 'removed from'} ${updatedUser.name}.`,
        status: 'success',
        duration: 3000,
        isClosable: true
      });
    } catch (err) {
      console.error('Error toggling admin status:', err);
      toast({
        title: 'Action failed',
        description: 'Failed to update user admin status.',
        status: 'error',
        duration: 5000,
        isClosable: true
      });
    } finally {
      setProcessing(prev => ({ ...prev, [userId]: false }));
    }
  };

  const handleTogglePermissions = async (userId) => {
    try {
      setProcessing(prev => ({ ...prev, [`perm_${userId}`]: true }));
      
      const updatedUser = await userService.togglePermissions(userId);
      
      // Update the local state
      setUsers(prevUsers =>
        prevUsers.map(user =>
          user.id === userId ? { ...user, is_read_only: updatedUser.is_read_only } : user
        )
      );
      
      toast({
        title: `User permissions updated`,
        description: `${updatedUser.name} now has ${updatedUser.is_read_only ? 'read-only' : 'read-write'} access.`,
        status: 'success',
        duration: 3000,
        isClosable: true
      });
    } catch (err) {
      console.error('Error toggling user permissions:', err);
      toast({
        title: 'Action failed',
        description: 'Failed to update user permissions.',
        status: 'error',
        duration: 5000,
        isClosable: true
      });
    } finally {
      setProcessing(prev => ({ ...prev, [`perm_${userId}`]: false }));
    }
  };

  const handleApproveUser = async (userId) => {
    try {
      setProcessing(prev => ({ ...prev, [`approve_${userId}`]: true }));
      
      const approvedUser = await userService.approveUser(userId);
      
      // Remove from pending list
      setPendingUsers(prevPendingUsers => 
        prevPendingUsers.filter(user => user.id !== userId)
      );
      
      // Refresh the users list to include the newly approved user
      fetchUsers();
      
      toast({
        title: 'User approved',
        description: `${approvedUser.name} (${approvedUser.email}) has been approved.`,
        status: 'success',
        duration: 3000,
        isClosable: true
      });
    } catch (err) {
      console.error('Error approving user:', err);
      toast({
        title: 'Action failed',
        description: 'Failed to approve user.',
        status: 'error',
        duration: 5000,
        isClosable: true
      });
    } finally {
      setProcessing(prev => ({ ...prev, [`approve_${userId}`]: false }));
    }
  };

  const handleRejectUser = async (userId, userEmail) => {
    try {
      setProcessing(prev => ({ ...prev, [`reject_${userId}`]: true }));
      
      await userService.rejectUser(userId);
      
      // Remove from pending list
      setPendingUsers(prevPendingUsers => 
        prevPendingUsers.filter(user => user.id !== userId)
      );
      
      toast({
        title: 'User rejected',
        description: `${userEmail} has been rejected.`,
        status: 'info',
        duration: 3000,
        isClosable: true
      });
    } catch (err) {
      console.error('Error rejecting user:', err);
      toast({
        title: 'Action failed',
        description: 'Failed to reject user.',
        status: 'error',
        duration: 5000,
        isClosable: true
      });
    } finally {
      setProcessing(prev => ({ ...prev, [`reject_${userId}`]: false }));
    }
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    
    try {
      setProcessing(prev => ({ ...prev, [`delete_${userToDelete.id}`]: true }));
      
      await userService.deleteUser(userToDelete.id);
      
      // Remove from users list
      setUsers(prevUsers => 
        prevUsers.filter(user => user.id !== userToDelete.id)
      );
      
      toast({
        title: 'User deleted',
        description: `${userToDelete.name} (${userToDelete.email}) has been deleted.`,
        status: 'success',
        duration: 3000,
        isClosable: true
      });
      
      onClose();
    } catch (err) {
      console.error('Error deleting user:', err);
      toast({
        title: 'Action failed',
        description: 'Failed to delete user.',
        status: 'error',
        duration: 5000,
        isClosable: true
      });
    } finally {
      setProcessing(prev => ({ ...prev, [`delete_${userToDelete.id}`]: false }));
      setUserToDelete(null);
    }
  };
  
  const openDeleteModal = (user) => {
    setUserToDelete(user);
    onOpen();
  };

  if (loading) {
    return (
      <Box textAlign="center" py={10}>
        <Spinner size="xl" />
        <Text mt={4}>Loading users...</Text>
      </Box>
    );
  }

  return (
    <Box>
      <Heading as="h1" size="xl" mb={4}>Admin Panel</Heading>
      <Text mb={4}>Manage users and view audit logs for environment file changes.</Text>
      
      <Tabs variant="enclosed" colorScheme="teal" mb={8}>
        <TabList>
          <Tab>User Management</Tab>
          <Tab>Approval Requests {pendingUsers.length > 0 && <Badge colorScheme="red" ml={2}>{pendingUsers.length}</Badge>}</Tab>
          <Tab>Audit Logs</Tab>
        </TabList>
        
        <TabPanels>
          {/* User Management Tab */}
          <TabPanel>
            <Heading as="h2" size="md" mb={4}>User Management</Heading>
            {loading ? (
              <Box textAlign="center" py={10}>
                <Spinner size="xl" />
                <Text mt={4}>Loading users...</Text>
              </Box>
            ) : error ? (
              <Alert status="error" mb={6} rounded="md">
                <AlertIcon />
                {error}
              </Alert>
            ) : (
              <Box overflowX="auto">
                <Table variant="simple">
                  <Thead>
                    <Tr>
                      <Th>User</Th>
                      <Th>Email</Th>
                      <Th>Role</Th>
                      <Th>Access Level</Th>
                      <Th>Permissions</Th>
                      <Th>Admin Status</Th>
                      <Th>Actions</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {users.length > 0 ? (
                      users.filter(user => user.is_approved).map((user) => (
                        <Tr key={user.id}>
                          <Td>
                            <HStack>
                              <Avatar size="sm" src={user.picture} name={user.name} />
                              <Text>{user.name}</Text>
                            </HStack>
                          </Td>
                          <Td>{user.email}</Td>
                          <Td>
                            <Badge colorScheme={user.is_admin ? 'purple' : 'green'}>
                              {user.is_admin ? 'Admin' : 'User'}
                            </Badge>
                          </Td>
                          <Td>
                            <Badge colorScheme={user.is_read_only ? 'blue' : 'orange'}>
                              {user.is_read_only ? 'Read Only' : 'Read-Write'}
                            </Badge>
                          </Td>
                          <Td>
                            <Switch
                              isChecked={!user.is_read_only}
                              onChange={() => handleTogglePermissions(user.id)}
                              isDisabled={processing[`perm_${user.id}`] || user.is_admin}
                              colorScheme="orange"
                            />
                          </Td>
                          <Td>
                            <Switch
                              isChecked={user.is_admin}
                              onChange={() => handleToggleAdmin(user.id)}
                              isDisabled={processing[user.id]}
                              colorScheme="purple"
                            />
                          </Td>
                          <Td>
                            <IconButton
                              aria-label="Delete user"
                              icon={<DeleteIcon />}
                              colorScheme="red"
                              variant="outline"
                              size="sm"
                              onClick={() => openDeleteModal(user)}
                              isDisabled={processing[`delete_${user.id}`]}
                            />
                          </Td>
                        </Tr>
                      ))
                    ) : (
                      <Tr>
                        <Td colSpan={6} textAlign="center">No users found</Td>
                      </Tr>
                    )}
                  </Tbody>
                </Table>
              </Box>
            )}
          </TabPanel>

          {/* User Approval Requests Tab */}
          <TabPanel>
            <Heading as="h2" size="md" mb={2}>User Approval Requests</Heading>
            <Text mb={4}>
              Review and approve new user registration requests. Only users with @wishfin.com email addresses can register.
            </Text>
            
            {pendingLoading ? (
              <Box textAlign="center" py={10}>
                <Spinner size="xl" />
                <Text mt={4}>Loading user requests...</Text>
              </Box>
            ) : pendingError ? (
              <Alert status="error" mb={6} rounded="md">
                <AlertIcon />
                {pendingError}
              </Alert>
            ) : (
              <Box overflowX="auto">
                {pendingUsers.length > 0 ? (
                  <Table variant="simple">
                    <Thead>
                      <Tr>
                        <Th>User</Th>
                        <Th>Email</Th>
                        <Th>Requested On</Th>
                        <Th width="200px">Actions</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {pendingUsers.map((user) => (
                        <Tr key={user.id}>
                          <Td>
                            <HStack>
                              <Avatar size="sm" src={user.picture} name={user.name} />
                              <Text>{user.name}</Text>
                            </HStack>
                          </Td>
                          <Td>{user.email}</Td>
                          <Td>{new Date(user.created_at).toLocaleString()}</Td>
                          <Td>
                            <ButtonGroup size="sm" isAttached variant="outline">
                              <Button
                                colorScheme="green"
                                onClick={() => handleApproveUser(user.id)}
                                isLoading={processing[`approve_${user.id}`]}
                              >
                                Approve
                              </Button>
                              <Button
                                colorScheme="red"
                                onClick={() => handleRejectUser(user.id, user.email)}
                                isLoading={processing[`reject_${user.id}`]}
                              >
                                Reject
                              </Button>
                            </ButtonGroup>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                ) : (
                  <Alert status="info" variant="subtle">
                    <AlertIcon />
                    No pending user approval requests.
                  </Alert>
                )}
              </Box>
            )}
          </TabPanel>
          
          {/* Audit Logs Tab */}
          <TabPanel>
            <Heading as="h2" size="md" mb={4}>Environment File Audit Logs</Heading>
            <Text mb={4}>
              Track changes made to .env files. Shows the last 20 changes across all environment files.
            </Text>
            
            {auditLoading ? (
              <Box textAlign="center" py={10}>
                <Spinner size="xl" />
                <Text mt={4}>Loading audit logs...</Text>
              </Box>
            ) : auditError ? (
              <Alert status="error" mb={6} rounded="md">
                <AlertIcon />
                {auditError}
              </Alert>
            ) : (
              <AuditLogTable auditLogs={auditLogs} />
            )}
          </TabPanel>
        </TabPanels>
      </Tabs>
      
      {/* Delete User Confirmation Modal */}
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Delete User</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {userToDelete && (
              <>
                <Text>Are you sure you want to delete this user?</Text>
                <Text fontWeight="bold" mt={2}>{userToDelete.name}</Text>
                <Text>{userToDelete.email}</Text>
                <Text fontSize="sm" mt={3} color="red.500">
                  This action cannot be undone. The user will be permanently removed from the system.
                  Their audit logs will be preserved.
                </Text>
              </>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
            <Button 
              colorScheme="red" 
              onClick={handleDeleteUser}
              isLoading={userToDelete && processing[`delete_${userToDelete.id}`]}
            >
              Delete User
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

export default AdminPanel;
